import React from 'react';
import type { SleepStagesWearableMetricDataset, NumericWearableMetricDataset, WearableMetricDataset } from '../../services/memberWearableMetricsService';

const mutedTextColor = 'var(--melio-text-muted)';

function clampRange(min: number, max: number) {
  if (min === max) {
    return { min: min - 1, max: max + 1 };
  }
  const padding = Math.max((max - min) * 0.12, 1);
  return { min: min - padding, max: max + padding };
}

function NumericMetricChart({ dataset }: { dataset: NumericWearableMetricDataset }) {
  if (!dataset.points.length) {
    return <div className="h-48 flex items-center justify-center text-sm" style={{ color: mutedTextColor }}>No wearable data yet.</div>;
  }

  const width = 520;
  const height = 210;
  const left = 48;
  const top = 14;
  const right = 18;
  const bottom = 24;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const values = dataset.points.map((point) => point.value);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const { min, max } = clampRange(rawMin, rawMax);
  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  const averageY = top + ((max - average) / (max - min)) * chartHeight;

  const pointsWithCoords = dataset.points.map((point, index) => {
    const x = left + (dataset.points.length === 1 ? chartWidth / 2 : (index / (dataset.points.length - 1)) * chartWidth);
    const y = top + ((max - point.value) / (max - min)) * chartHeight;
    return { ...point, x, y };
  });

  const path = pointsWithCoords.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`).join(' ');

  if (dataset.chartType === 'bar') {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-48" aria-hidden="true">
        {[0, 1, 2, 3].map((tick) => {
          const y = top + (tick / 3) * chartHeight;
          const labelValue = max - ((max - min) * tick) / 3;
          return (
            <g key={tick}>
              <line x1={left} y1={y} x2={width - right} y2={y} stroke="rgba(26, 26, 24,0.08)" strokeWidth="1" />
              <text x={8} y={y + 4} fontSize="11" fill={mutedTextColor}>{Math.round(labelValue)}</text>
            </g>
          );
        })}
        {dataset.points.map((point, index) => {
          const barWidth = Math.max(8, chartWidth / Math.max(dataset.points.length * 1.8, 1));
          const x = left + ((index + 0.5) / dataset.points.length) * chartWidth - barWidth / 2;
          const y = top + ((max - point.value) / (max - min)) * chartHeight;
          return (
            <rect
              key={point.key}
              x={x}
              y={y}
              width={barWidth}
              height={top + chartHeight - y}
              rx="6"
              fill={dataset.definition.accentColor}
              opacity={index % 3 === 0 ? 0.75 : 0.92}
            />
          );
        })}
        <line x1={left} y1={averageY} x2={width - right} y2={averageY} stroke="rgba(196,164,132,0.72)" strokeWidth="1.5" strokeDasharray="5 5" />
        <text x={left} y={Math.max(13, averageY - 8)} fontSize="10.5" fill={mutedTextColor}>AVG.</text>
        {dataset.points.map((point, index) => (
          <text key={`${point.key}-label`} x={left + ((index + 0.5) / dataset.points.length) * chartWidth} y={height - 6} textAnchor="middle" fontSize="11" fill={mutedTextColor}>
            {index === 0 || index === dataset.points.length - 1 || index === Math.floor(dataset.points.length / 2) ? point.label : ''}
          </text>
        ))}
      </svg>
    );
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-48" aria-hidden="true">
      {[0, 1, 2, 3].map((tick) => {
        const y = top + (tick / 3) * chartHeight;
        const labelValue = max - ((max - min) * tick) / 3;
        return (
          <g key={tick}>
            <line x1={left} y1={y} x2={width - right} y2={y} stroke="rgba(26, 26, 24,0.08)" strokeWidth="1" />
            <text x={8} y={y + 4} fontSize="11" fill={mutedTextColor}>{Math.round(labelValue * 10) / 10}</text>
          </g>
        );
      })}
      <line x1={left} y1={averageY} x2={width - right} y2={averageY} stroke="rgba(196,164,132,0.72)" strokeWidth="1.5" strokeDasharray="5 5" />
      <text x={left} y={Math.max(13, averageY - 8)} fontSize="10.5" fill={mutedTextColor}>AVG.</text>
      <path d={path} fill="none" stroke={dataset.definition.accentColor} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      {pointsWithCoords.map((point) => (
        <circle key={point.key} cx={point.x} cy={point.y} r="4.5" fill={dataset.definition.accentColor} />
      ))}
      {pointsWithCoords.map((point, index) => (
        <text key={`${point.key}-label`} x={point.x} y={height - 6} textAnchor="middle" fontSize="11" fill={mutedTextColor}>
          {index === 0 || index === pointsWithCoords.length - 1 || index === Math.floor(pointsWithCoords.length / 2) ? point.label : ''}
        </text>
      ))}
    </svg>
  );
}

function SleepStagesChart({ dataset }: { dataset: SleepStagesWearableMetricDataset }) {
  if (!dataset.points.length) {
    return <div className="h-48 flex items-center justify-center text-sm" style={{ color: mutedTextColor }}>No wearable data yet.</div>;
  }

  const max = Math.max(...dataset.points.map((point) => point.light + point.deep + point.rem + point.awake));
  /**
   * Sleep stages are ORDINAL — awake, light, REM, deep is a depth scale, not a
   * set of unrelated categories. So this is a sequential single-hue ramp in the
   * brand's Oxygen family (light -> dark = shallow -> deep), with `awake` as a
   * neutral rather than a fifth colour. The previous three arbitrary hues
   * implied the stages were unrelated kinds rather than degrees.
   *
   * Validated as an ordinal ramp: lightness monotone, every adjacent dL >= 0.06,
   * light end 2.30:1 against a white surface, hue spread 16 degrees.
   */
  const series = [
    { key: 'deep', label: 'Deep', color: '#12233D' },
    { key: 'rem', label: 'REM', color: '#2E5677' },
    { key: 'light', label: 'Light', color: '#5C87A8' },
    { key: 'awake', label: 'Awake', color: 'rgba(26, 26, 24, 0.16)' },
  ] as const;

  return (
    <div>
      <div className="flex items-end gap-2 h-48 pt-6">
        {dataset.points.map((point) => {
          const total = point.light + point.deep + point.rem + point.awake;
          return (
            <div key={point.key} className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full h-full flex flex-col justify-end gap-1">
                {series.map((segment) => {
                  const value = point[segment.key];
                  const heightPct = total > 0 ? (value / max) * 100 : 0;
                  return (
                    <div
                      key={segment.key}
                      className="w-full rounded-md"
                      style={{
                        minHeight: value > 0 ? 8 : 0,
                        height: `${Math.max(0, heightPct)}%`,
                        backgroundColor: segment.color,
                      }}
                    />
                  );
                })}
              </div>
              <div className="text-xs" style={{ color: mutedTextColor }}>{point.label}</div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 mt-4">
        {series.map((segment) => (
          <div key={segment.key} className="inline-flex items-center gap-2 text-xs" style={{ color: mutedTextColor }}>
            <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: segment.color }} />
            {segment.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WearableMetricChart({ dataset }: { dataset: WearableMetricDataset }) {
  if (dataset.chartType === 'sleep_stages') {
    return <SleepStagesChart dataset={dataset} />;
  }

  return <NumericMetricChart dataset={dataset} />;
}
